"use client";

import React from "react";
import { ArrowRight, SlidersHorizontal, ShieldCheck, CircleCheck as CheckCircle2, Layers } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
import Reveal from "@/components/common/Reveal";
import { useLanguage } from "@/hooks/useLanguage";
import { pick } from "@/lib/i18n";
import { COMMON } from "@/lib/commonCopy";
export default function ApplicationsIntro() {
  const {
    lang
  } = useLanguage();
  const technicalHighlights = [{
    label: pick(lang, {
      en: "Woven Retention",
      zh: "编织过滤精度",
      es: "Retención de tejido",
      de: "Gewebe-Filterfeinheit",
      fr: "Rétention tissée",
      pt: "Retenção tecida"
    }),
    val: "1 µm – 25 mm"
  }, {
    label: pick(lang, {
      en: "Sintered Porous",
      zh: "烧结多孔精度",
      es: "Poros sinterizados",
      de: "Sintermedien",
      fr: "Médias frittés",
      pt: "Meios sinterizados"
    }),
    val: "1 – 200 µm"
  }, {
    label: pick(lang, {
      en: "Wedge Wire Slot",
      zh: "楔形条缝缝隙",
      es: "Ranura en cuña",
      de: "Spaltsieb-Weite",
      fr: "Fente profilée",
      pt: "Fenda em cunha"
    }),
    val: "0.02 – 5.0 mm"
  }, {
    label: pick(lang, {
      en: "Standard Alloys",
      zh: "标准工程合金",
      es: "Aleaciones estándar",
      de: "Standardlegierungen",
      fr: "Alliages standards",
      pt: "Ligas padrão"
    }),
    val: "304 / 316L / Alloys"
  }];
  const badgeCategory = pick(lang, {
    en: "Industrial Applications & Engineering",
    zh: "工业过滤、筛分与分离应用",
    es: "Aplicaciones industriales e ingeniería",
    de: "Industrielle Anwendungen & Engineering",
    fr: "Applications industrielles et ingénierie",
    pt: "Aplicações industriais e engenharia"
  });
  const headingText = pick(lang, {
    en: "Filtration, Screening & Separation Applications",
    zh: "过滤、筛分与分离工业应用",
    es: "Aplicaciones de filtración, tamizado y separación",
    de: "Anwendungen für Filtration, Siebung und Trennung",
    fr: "Applications de filtration, tamisage et séparation",
    pt: "Aplicações de filtração, peneiramento e separação"
  });
  const leadText = pick(lang, {
    en: "MutualFilter supplies high-performance stainless steel woven wire mesh, sintered media, and fabricated screen components across chemical processing, polymer melt extrusion, pharmaceutical, and water treatment operations. Engineered for extreme differential pressure, thermal stability, and precise retention.",
    zh: "MutualFilter 为全球化工流程、聚合物挤出、食品医药、水处理及重工筛分行业提供高性能不锈钢编织丝网与烧结过滤元件。无论面对极高工作压差、高温腐蚀还是微米级拦截需求，我们的工程技术团队均可按需定制匹配。",
    es: "MutualFilter suministra malla de alambre tejida de acero inoxidable de alto rendimiento, medios sinterizados y componentes de tamizado fabricados para procesamiento químico, extrusión de polímeros fundidos, industria farmacéutica y tratamiento de aguas. Diseñados para soportar presiones diferenciales extremas, estabilidad térmica y retención precisa.",
    de: "MutualFilter liefert hochleistungsfähiges Edelstahl-Drahtgewebe, Sintermedien und gefertigte Siebkomponenten für die chemische Verfahrenstechnik, Polymer-Schmelzeextrusion, Pharmazeutik und Wasseraufbereitung. Entwickelt für extreme Differenzdrücke, thermische Stabilität und präzise Partikelabscheidung.",
    fr: "MutualFilter fournit des toiles métalliques tissées en acier inoxydable haute performance, des médias frittés et des composants filtrants fabriqués pour les procédés chimiques, l'extrusion de polymères fondus, la pharmacie et le traitement de l'eau. Conçus pour les pressions différentielles extrêmes, la stabilité thermique et une rétention précise.",
    pt: "A MutualFilter fornece malha de arame tecida de aço inoxidável de alto desempenho, meios sinterizados e componentes de filtragem fabricados para processamento químico, extrusão de polímeros fundidos, farmacêutica e tratamento de água. Desenvolvidos para resistir a pressões diferenciais extremas, estabilidade térmica e retenção precisa."
  });
  const ctaQuote = pick(lang, COMMON.requestQuote);
  const ctaProducts = pick(lang, COMMON.exploreProducts);
  const imgAlt = pick(lang, {
    en: "Industrial stainless steel filter mesh and filtration elements used in separation equipment",
    zh: "用于分离设备的工业级不锈钢过滤网及过滤元件",
    es: "Malla filtrante y elementos de filtración de acero inoxidable industrial utilizados en equipos de separación",
    de: "Industrielle Edelstahl-Filtergewebe und Filtrationselemente für Trennanlagen",
    fr: "Toile filtrante et éléments de filtration en acier inoxydable industriel utilisés dans les équipements de séparation",
    pt: "Malha filtrante e elementos de filtração de aço inoxidável industrial usados em equipamentos de separação"
  });
  const imgBadge = pick(lang, {
    en: "Custom Meshes & Laminate Elements",
    zh: "定制金属网孔与层压滤芯",
    es: "Mallas a medida y elementos laminados",
    de: "Kundenspezifische Drahtgewebe & Laminatelemente",
    fr: "Toiles sur mesure et éléments stratifiés",
    pt: "Malhas sob medida e elementos laminados"
  });
  const bottomNote = pick(lang, {
    en: "Rolls, cylinders, baskets & OEM packs",
    zh: "支持原卷、滤筒、筛篮与定制组件",
    es: "Rollos, cilindros, cestas y paquetes OEM",
    de: "Rollen, Zylinder, Körbe & OEM-Pakete",
    fr: "Rouleaux, cylindres, paniers & packs OEM",
    pt: "Rolos, cilindros, cestos e pacotes OEM"
  });
  const oemCustomLabel = pick(lang, COMMON.oemCustom);
  return <section id="industrialapplicationsintro" data-nav="light" className="relative overflow-hidden bg-background text-foreground border-b border-border mf-hero">
      <div className="relative mf-container">
        <div className="grid grid-cols-1 items-center gap-8 lg:grid-cols-12 lg:gap-8">
          {/* Left Column (7 cols on lg) */}
          <div className="lg:col-span-7 flex flex-col items-start text-left">
            <Reveal>
              <div>
                {/* Eyebrow Badges */}
                <div className="mb-4 flex flex-wrap items-center gap-2">
                  <Badge variant="outline" className="gap-1.5 rounded-md border-border bg-muted px-3 py-1 font-mono tracking-tight text-foreground mf-caption">
                    <SlidersHorizontal className="size-3.5 text-primary" />
                    <span>{badgeCategory}</span>
                  </Badge>
                  <Badge variant="outline" className="gap-1 rounded-md border border-border bg-muted px-2.5 py-1 font-mono text-foreground mf-caption">
                    <ShieldCheck className="size-3 text-primary inline" />
                    <span>ISO 9044 / ASTM E2016</span>
                  </Badge>
                </div>

                {/* Main Headline (single h1 on page) */}
                <h1 className="font-default text-balance mf-h1 text-foreground">
                  {headingText}
                </h1>

                {/* Supporting Lead */}
                <p className="mt-4 max-w-2xl leading-relaxed text-muted-foreground font-default mf-lead">
                  {leadText}
                </p>

                {/* 2 CTAs */}
                <div className="mt-6 flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full sm:w-auto">
                  <Button size="lg" asChild className="px-6 font-semibold transition-colors w-full sm:w-auto">
                    <Link to="/RequestAQuotePage" className="inline-flex items-center justify-center gap-2">
                      <span>{ctaQuote}</span>
                      <ArrowRight className="size-4" />
                    </Link>
                  </Button>
                  <Button variant="outline" size="lg" asChild className="px-6 font-semibold transition-colors w-full sm:w-auto bg-card">
                    <Link to="/ProductsPage" className="inline-flex items-center justify-center gap-2">
                      <Layers className="size-4" />
                      <span>{ctaProducts}</span>
                    </Link>
                  </Button>
                </div>
              </div>
            </Reveal>
          </div>

          {/* Right Column: Visual Card (5 cols on lg) */}
          <div className="lg:col-span-5 flex flex-col justify-center">
            <Reveal delay={120}>
              <Card className="overflow-hidden rounded-lg border border-border bg-card gap-0 py-0 text-card-foreground">
                {/* Product Visual Container */}
                <div className="relative mf-card-media mf-ratio-hero w-full overflow-hidden">
                  <img src="https://mutualfilter.com/wp-content/uploads/2026/09/sintered_mesh_filter_elements.png" alt={imgAlt} loading="eager" fetchPriority="high" decoding="async" className="h-full w-full object-cover" />
                  <div className="absolute top-3 left-3 bg-card border border-border px-3 py-1 rounded-md mf-caption font-mono text-foreground flex items-center gap-1.5">
                    <SlidersHorizontal className="size-3.5 text-primary" />
                    <span>{imgBadge}</span>
                  </div>
                </div>

                {/* Card Content Footer Row */}
                <CardContent className="p-4 bg-card">
                  <div className="flex items-center justify-between mf-caption">
                    <div className="flex items-center gap-1.5 text-muted-foreground">
                      <CheckCircle2 className="size-3.5 text-primary shrink-0" />
                      <span>{bottomNote}</span>
                    </div>
                    <Link to="/OEMCustomMeshEngineeringPage" className="font-medium text-primary hover:underline whitespace-nowrap ml-2 flex items-center gap-1 shrink-0">
                      <span>{oemCustomLabel}</span>
                      <ArrowRight className="size-3" />
                    </Link>
                  </div>
                </CardContent>
              </Card>
            </Reveal>
          </div>
        </div>

        {/* Spec Strip Below Grid */}
        <div className="mf-spec-strip">
          {technicalHighlights.map((spec, idx) => <div key={idx} className="mf-spec-strip__item">
              <div className="mf-spec-strip__label">
                {spec.label}
              </div>
              <div className="mf-spec-strip__value font-mono">
                {spec.val}
              </div>
            </div>)}
        </div>
      </div>
    </section>;
}